/* istanbul ignore file */
import React from 'react'
import { match, Pattern } from 'ts-pattern'
import * as graph from '@adc/sdk'
import {
  type AuctionBlueprintValidationResult,
  formatters,
} from '@adc/blueprint'
import classnames from 'classnames/bind'
import {
  PhoneVerification,
  GridDeprecated,
  Image,
  Text,
  utils,
  BaseRadioGroupFieldVariant,
  constants,
} from '@adc/ui-adc'
import _ from 'lodash'
import { Maybe } from '@adc/sdk/types'
import { checkAvailabilityAndStandardizeAddress } from 'common/providers/privateSeller'
import {
  ADDRESS_ALREADY_USED_MESSAGE,
  ADDRESS_ERROR_MESSAGE,
} from 'common/constants/messages'
import { determineNextStep } from '../utils/workflowNavigation'
import wellMaintainedImage from '../../../assets/well-maintained.png'
import datedImage from '../../../assets/dated.jpeg'
import fixerUpperImage from '../../../assets/fixer-upper.jpeg'
import otherImage from '../../../assets/other.jpg'
import ContactInformationFooter from './contactInformation/footer'
import ContactInformationSubtitle from './contactInformation/subtitle'
import {
  mapLeadAddressToStandardPropertyAddress,
  mapStandardPropertyAddressToLeadAddress,
} from '../../../utils/addressMapping'
import smartsaleLogo from './smartsale.svg'
import styles from './styles.scss'
import { StepId, NextStepData, SaleTimeline } from './types'
import PasswordlessSignupHeader, {
  PasswordlessSignupSubHeader,
} from './customHeaders/passwordlessSignup'

const { formatEnum } = formatters
const { Row, Column } = GridDeprecated
const { ColorNames } = constants
const cx = classnames.bind(styles)
const { formValidators } = utils
const numberValueParser = (value: string): number | null =>
  parseFloat(value.replace(/[$,]/g, '')) || null
const isLandPropertyType = (leadIntake: Record<string, any>): boolean =>
  leadIntake?.['property.property_type'] === 'LAND'
export const isUserLoggedIn = (leadIntake: Record<string, any>): boolean => {
  return Boolean(leadIntake?.__computed__?.logged_in)
}
const contactMethodOptions = [
  {
    key: graph.enums.CommunicationMethod.PhoneCall,
    value: graph.enums.CommunicationMethod.PhoneCall,
    text: 'Phone',
  },
  {
    key: graph.enums.CommunicationMethod.Email,
    value: graph.enums.CommunicationMethod.Email,
    text: 'Email',
  },
  {
    key: graph.enums.CommunicationMethod.PhoneText,
    value: graph.enums.CommunicationMethod.PhoneText,
    text: 'Text',
  },
]

const propertyTypeOptions = [
  graph.enums.PropertyStructureType.SingleFamilyHome,
  graph.enums.PropertyStructureType.Land,
  graph.enums.PropertyStructureType.CondominiumUnit,
  graph.enums.PropertyStructureType.Townhouse,
  graph.enums.PropertyStructureType.ManufacturedHome,
  graph.enums.PropertyStructureType.MobileHome,
  graph.enums.PropertyStructureType.ModularHome,
  graph.enums.PropertyStructureType.SingleFamilyAttached,
  graph.enums.PropertyStructureType.SingleFamilyDetached,
  graph.enums.PropertyStructureType.ThreePlexBuilding,
  graph.enums.PropertyStructureType.HiRiseCondoUnit,
  graph.enums.PropertyStructureType.CoopUnit,
  graph.enums.PropertyStructureType.DuplexUnit,
  graph.enums.PropertyStructureType.MultiUnitBuilding,
  graph.enums.PropertyStructureType.PudHome,
  graph.enums.PropertyStructureType.Other,
  graph.enums.PropertyStructureType.FourPlexBuilding,
  graph.enums.PropertyStructureType.ApartmentUnit,
  graph.enums.PropertyStructureType.ApartmentBuilding,
  graph.enums.PropertyStructureType.ApartmentComplex,
  graph.enums.PropertyStructureType.FourPlexUnit,
  graph.enums.PropertyStructureType.ThreePlexUnit,
  graph.enums.PropertyStructureType.AttachedGarage,
  graph.enums.PropertyStructureType.CoopBuilding,
  graph.enums.PropertyStructureType.CondominiumBuilding,
  graph.enums.PropertyStructureType.DetachedGarage,
  graph.enums.PropertyStructureType.DuplexBuilding,
  graph.enums.PropertyStructureType.Floor,
  graph.enums.PropertyStructureType.Studio,
  graph.enums.PropertyStructureType.Loft,
]

export enum QuestionType {
  Input = 'INPUT',
  Number = 'NUMBER',
  Address = 'ADDRESS',
  Map = 'MAP',
  Radio = 'RADIO',
  GroupRadio = 'GROUP_RADIO',
  Select = 'SELECT',
  Currency = 'CURRENCY',
  PhoneNumber = 'PHONE_NUMBER',
  Authentication = 'AUTHENTICATION',
  Checkbox = 'CHECKBOX',
  RadioWithDetails = 'RADIO_WITH_DETAILS',
  Password = 'PASSWORD',
}

export type Question = {
  type: QuestionType
  field: string
  validate?: (
    answers: Record<string, any>
  ) =>
    | AuctionBlueprintValidationResult
    | Promise<AuctionBlueprintValidationResult>
  label?: string
  placeholder?: string
  options?: Record<string, string | boolean | string[]>[]
  valueParser?: (value: any) => any
  styles?: Array<string>
  maskOptions?: Record<string, string | boolean | number>
  showIncrementButtons?: boolean
  isRequired?: boolean | ((leadIntake: Record<string, any>) => boolean)
  disabled?: boolean | ((leadIntake: Record<string, any>) => boolean)
  readOnly?: boolean | ((leadIntake: Record<string, any>) => boolean)
  validateOnlyOnBlur?: boolean
  componentProps?: Record<string, any>
  defaultValue?: string | ((answers: Record<string, any>) => string | null)
  compact: boolean
}

export type GAMetadata = {
  virtualTitle: string
  virtualUrl: string
}

export type Step = {
  id: StepId
  title: string | ((featureFlags: Record<string, any>) => React.ReactNode)
  path: string
  subtitle?: string | ((answer: Record<string, string>) => React.ReactNode)
  next: (arg0: NextStepData) => StepId
  text?: (
    featureFlags: Record<string, any>,
    listingId: string
  ) => React.ReactNode
  questions?: Question[]
  stepStyles?: Array<string>
  isLastStep?: (answer: Record<string, string>) => boolean
  isSubmitStep?: (answer: Record<string, string>) => boolean
  hidePrevious?: boolean
  disabled?: (answer?: Record<string, string>) => boolean
  ga?: GAMetadata
  resetFieldsWhenChanged: string[]
}

export type Bound = {
  value: number
  inclusive: boolean
  bounded: boolean
}
type ValidationResult = { valid: boolean; message: string }

export const validValidation: ValidationResult = { valid: true, message: '' }

const requiredCheckboxValidator = (
  value: any,
  errorMessage?: string
): ValidationResult =>
  _.isNil(value) || value !== true
    ? { message: errorMessage || '', valid: false }
    : validValidation

const requiredValueValidator = (
  value: any,
  errorMessage?: string
): ValidationResult =>
  _.isNil(value) || /^\s*$/.test(value)
    ? { message: errorMessage || 'Required', valid: false }
    : validValidation

const requiredFieldValidator =
  (field: string) =>
  (answers: Record<string, string>): ValidationResult => {
    const isPropertyTypeLand = answers['property.property_type'] === 'LAND'
    // @ts-ignore next as match ts pattern is complaining
    return match({ field, isPropertyTypeLand })
      .with({ isPropertyTypeLand: true, field: 'property.lot_size' }, () =>
        requiredValueValidator(answers[field])
      )
      .with(
        { isPropertyTypeLand: false, field: 'property.lot_size' },
        () => validValidation
      )
      .with(
        {
          isPropertyTypeLand: true,
          field: Pattern.when(
            (
              field: string
            ): field is Exclude<string, 'property.relationship_to_property'> =>
              field !== 'property.relationship_to_property'
          ),
        },
        () => validValidation
      )
      .with({ field: '__computed__.privacy_statement' }, () =>
        requiredCheckboxValidator(answers[field])
      )
      .otherwise(() => requiredValueValidator(answers[field]))
  }

const requiredAddressFields = [
  { field: '__computed__.property.addressDisplayText', errorText: 'Required' },
  { field: 'property.address', errorText: 'Please select a suggested address' },
  { field: 'property.city', errorText: 'Address must contain City' },
  { field: 'property.state', errorText: 'Address must contain State' },
  {
    field: 'property.postal_code',
    errorText: 'Address must contain Zip Code',
  },
]

export const addressValidator = async (value: Record<string, any>) => {
  const valueValidation = requiredValueValidator(
    value['__computed__.property.addressDisplayText'],
    ADDRESS_ERROR_MESSAGE
  )

  if (!valueValidation.valid) return valueValidation

  if (value['property.county']?.includes(' County')) {
    // Remove ' County' from the county field if present to avoid inconsistencies with how we handle our data elsewhere
    value['property.county'] = value['property.county'].replace(' County', '')
  }

  if (
    !value.is_land_lead &&
    value['__computed__.property.is_option_selected']
  ) {
    for (let i = 0; i < requiredAddressFields.length; i++) {
      const currentFieldValidator = requiredAddressFields[i]
      const currentValidation = requiredValueValidator(
        value[currentFieldValidator.field],
        ADDRESS_ERROR_MESSAGE as string
      )
      if (!currentValidation.valid) {
        return { valid: false, message: currentFieldValidator.errorText }
      }
    }

    // My goodness, y u cause so much pain you lead address and not an actual address, y y y
    //
    // Also, this seems to be returning lots of false positives..
    // NOTE: For Jon. The graph type is too wide. ms-listing requires quite a bit of information here in order to validate
    // and the partial is too loose.
    const standardizedAddress = await checkAvailabilityAndStandardizeAddress(
      mapLeadAddressToStandardPropertyAddress(value)
    )

    const isAvailable = Boolean(standardizedAddress)

    if (isAvailable) {
      Object.assign(
        value,
        mapStandardPropertyAddressToLeadAddress(standardizedAddress)
      )
    }

    // TODO: Alright, so this is a field, validation, but we _also_ want to pop up a notifier with a contact number and
    // such. I exposed the shared number and put it in here so you have all the information you need...
    if (!isAvailable) {
      return {
        message: ADDRESS_ALREADY_USED_MESSAGE,
        valid: false,
      }
    }
  }

  // In the case where is a land we are disabling all other validations but if user needs to select a valid address from suggestions (that will always contain state)
  if (!requiredValueValidator(value['property.state']).valid) {
    return {
      valid: false,
    }
  }

  return validValidation
}

const phoneValidator = (value: string) =>
  PhoneVerification.utils.isValidPhone(value)
    ? validValidation
    : { message: 'Invalid format', valid: false }

export const createBound = (
  value: number,
  inclusive: boolean,
  bounded: boolean
): Bound => ({
  value,
  inclusive,
  bounded,
})

const emailValidator = (value: string) => {
  if (!value) return validValidation
  if (value.includes('..')) {
    return { message: 'Email cannot contain consecutive dots', valid: false }
  }
  // @ts-ignore next
  const isValid = formValidators.email.REG_EMAIL.test(value) // eslint-disable-line
  return isValid ? validValidation : { message: 'Invalid Email', valid: false }
}

const generatePropertyTypeOptions = () => {
  return propertyTypeOptions.map((propertyType: Maybe<string>) => {
    return {
      key: propertyType,
      value: propertyType,
      text: formatEnum(propertyType),
    }
  })
}

export const generateNextStep = (
  answer: Record<string, any>,
  stepId: StepId,
  featureFlags: Record<string, any>
) => {
  const isLandLead = answer?.is_land_lead
  const hasMultipleProperties = answer?.['__computed__.are_multiple_properties']

  if (hasMultipleProperties && stepId === StepId.ConfirmAddress) {
    return StepId.MultipleProperties
  }

  if (
    !featureFlags['fe.resi.enable-initial-checkboxes-in-conv-flow'] &&
    stepId === StepId.ConfirmAddress
  ) {
    return StepId.MultipleProperties
  }

  if (
    !featureFlags['fe.resi.enable-initial-checkboxes-in-conv-flow'] &&
    stepId === StepId.MultipleProperties
  ) {
    return StepId.HomeSale
  }

  if (
    isLandLead &&
    hasMultipleProperties &&
    stepId === StepId.MultipleProperties
  ) {
    return StepId.RelationToProperty
  }

  if (hasMultipleProperties && stepId === StepId.MultipleProperties) {
    return StepId.HomeSale
  }

  if (
    !isLandLead &&
    !hasMultipleProperties &&
    stepId === StepId.ConfirmAddress
  ) {
    return StepId.HomeSale
  }

  return StepId.RelationToProperty
}

export const sellerFlowPath = '/sell-your-property'

// @ts-ignore
export const sellerSteps: Map<StepId, Step> = new Map<StepId, Step>([
  [
    StepId.Address,
    {
      id: StepId.Address,
      path: `${sellerFlowPath}/property-address`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.Address, answer, featureFlags, sellerSteps),
      title: 'What is the address of the home you are selling?',
      subtitle:
        'To begin, we need a little information.  It takes just 2 minutes.',
      questions: [
        {
          placeholder: 'Enter your address',
          field: '__computed__.property.addressDisplayText',
          type: QuestionType.Address,
          validate: addressValidator,
        },
        {
          placeholder:
            'I have land to sell and I am going to enter an approximate address above',
          field: 'is_land_lead',
          type: QuestionType.Checkbox,
        },
        {
          placeholder:
            'I have multiples properties to sell; I will enter one here and add more later',
          field: '__computed__.are_multiple_properties',
          type: QuestionType.Checkbox,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property - Address',
        virtualUrl: 'sell-your-property/property-address',
      },
    },
  ],
  [
    StepId.ConfirmAddress,
    {
      id: StepId.ConfirmAddress,
      path: `${sellerFlowPath}/address-confirmation`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.ConfirmAddress,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Is this the correct address?',
      questions: [
        {
          field: '__computed__.address_confirmed',
          type: QuestionType.Map,
          styles: ['full-height', 'mobile', 'map-field'],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Address Confirmation',
        virtualUrl: 'sell-your-property/address-confirmation',
      },
    },
  ],
  [
    StepId.HomeSale,
    {
      id: StepId.HomeSale,
      path: `${sellerFlowPath}/home-sale`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.HomeSale, answer, featureFlags, sellerSteps),
      title: 'Please select/enter details regarding the property',
      questions: [
        {
          label: 'Property Type',
          field: 'property.property_type',
          type: QuestionType.Select,
          validate: requiredFieldValidator('property.property_type'),
          options: generatePropertyTypeOptions(),
          isRequired: true,
        },
        {
          label: 'Bedrooms',
          field: 'property.bedrooms',
          validate: requiredFieldValidator('property.bedrooms'),
          type: QuestionType.Number,
          maskOptions: {
            allowNegative: false,
            allowDecimal: false,
            integerLimit: 2,
          },
          showIncrementButtons: true,
          disabled: leadIntake => isLandPropertyType(leadIntake),
          isRequired: leadIntake => !isLandPropertyType(leadIntake),
        },
        {
          label: 'Bathrooms',
          field: 'property.bathrooms',
          validate: (answers: Record<string, string>) => {
            const baseValidation =
              requiredFieldValidator('property.bathrooms')(answers)
            if (!baseValidation.valid) return baseValidation
            const value = answers['property.bathrooms']
            const parsed = parseFloat(value.replace(/[$,]/g, ''))
            if (parsed === 0) {
              return {
                valid: false,
                message: 'Bathrooms must be greater than 0',
              }
            }
            return validValidation
          },
          type: QuestionType.Number,
          maskOptions: {
            allowDecimal: true,
            allowNegative: false,
            decimalLimit: 2,
            roundToNearestFraction: 0.25,
            integerLimit: 3,
          },
          showIncrementButtons: true,
          disabled: leadIntake => isLandPropertyType(leadIntake),
          isRequired: leadIntake => !isLandPropertyType(leadIntake),
        },
        {
          label: 'Approximate Finished Square Footage',
          field: 'property.home_square_footage',
          validate: requiredFieldValidator('property.home_square_footage'),
          type: QuestionType.Number,
          maskOptions: {
            allowNegative: false,
            allowDecimal: false,
            includeThousandsSeparator: true,
            thousandsSeparatorSymbol: ',',
            integerLimit: 5,
          },
          valueParser: numberValueParser,
          disabled: leadIntake => isLandPropertyType(leadIntake),
          isRequired: leadIntake => !isLandPropertyType(leadIntake),
        },
        {
          label: 'Acres',
          field: 'property.lot_size',
          validate: requiredFieldValidator('property.lot_size'),
          type: QuestionType.Number,
          maskOptions: {
            allowDecimal: true,
            includeThousandsSeparator: true,
            thousandsSeparatorSymbol: ',',
            decimalLimit: 2,
            integerLimit: 9,
          },
          isRequired: leadIntake => isLandPropertyType(leadIntake),
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property - Home Sale',
        virtualUrl: 'sell-your-property/home-sale',
      },
    },
  ],
  [
    StepId.MultipleProperties,
    {
      id: StepId.MultipleProperties,
      path: `${sellerFlowPath}/multiple-properties`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.MultipleProperties,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'How many properties do you plan on selling?',
      questions: [
        {
          field: 'number_of_properties',
          validate: requiredFieldValidator('number_of_properties'),
          type: QuestionType.Number,
          maskOptions: {
            allowNegative: false,
            allowDecimal: false,
            allowLeadingZeroes: false,
          },
          showIncrementButtons: true,
          placeholder: '0 properties',
          valueParser: numberValueParser,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Multiple Properties',
        virtualUrl: 'sell-your-property/multiple-properties',
      },
    },
  ],
  [
    StepId.IntentSelector,
    {
      id: StepId.IntentSelector,
      path: `${sellerFlowPath}/intent`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.IntentSelector,
          answer,
          featureFlags,
          sellerSteps
        ),
      resetFieldsWhenChanged: ['property.sale_timeline'],
      title: 'I want SmartSale to help me…',
      questions: [
        {
          field: 'property.seller_intent',
          validate: requiredFieldValidator('property.seller_intent'),
          type: QuestionType.RadioWithDetails,
          options: [
            {
              title: 'Sell Fast',
              tooltip: 'Speed',
              tooltipColor: ColorNames.AdcLightBlue,
              icon: 'rocket',
              items: [
                'Sell my home as fast as possible',
                'Get great cash offers with no contingencies',
                'Skip open houses and showings',
                'Close quickly',
              ],
              value: graph.enums.SellerIntentType.Speed,
            },
            {
              title: 'Maximize Value',
              tooltip: 'Value',
              tooltipColor: ColorNames.AdcLightGreen,
              icon: 'money-bill',
              items: [
                'Get the highest price, even if it takes a bit longer',
                'Maximize my sale value by reaching the most qualified buyers',
                'Work with serious buyers, even if they need financing',
                'Get top dollar by allowing open houses, private tours, and inspections',
              ],
              value: graph.enums.SellerIntentType.Value,
            },
            {
              title: 'Stay Flexible',
              tooltip: 'Flexibility',
              icon: 'arrows-split-up-and-left',
              items: [
                'Avoid making repairs before I sell',
                'Stay in my home after the sale (rentback)',
                'Be open to standard buyer requests to secure stronger offers.',
                'Clearly understand all my selling options',
              ],
              value: graph.enums.SellerIntentType.Flexibility,
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Intent',
        virtualUrl: `${sellerFlowPath}/intent`,
      },
    },
  ],
  [
    StepId.Condition,
    {
      id: StepId.Condition,
      path: `${sellerFlowPath}/condition`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.Condition, answer, featureFlags, sellerSteps),
      title: 'What is the condition of the home?',
      questions: [
        {
          field: 'property.condition',
          validate: requiredFieldValidator('property.condition'),
          type: QuestionType.GroupRadio,
          componentProps: {
            variant: BaseRadioGroupFieldVariant.TWO_COLUMN,
          },
          options: [
            {
              label: 'Well-Maintained',
              subLabel: 'Move-in ready with quality finishes',
              image: wellMaintainedImage,
              value: graph.enums.PropertyCondition.WellMaintained,
            },
            {
              label: 'Dated',
              subLabel: 'Hasn’t been updated recently',
              image: datedImage,
              value: graph.enums.PropertyCondition.Dated,
            },
            {
              label: 'Fixer Upper',
              subLabel: 'Needs significant repairs',
              image: fixerUpperImage,
              value: graph.enums.PropertyCondition.FixerUpper,
            },
            {
              label: 'Other',
              subLabel: '(e.g. Land, Commercial)',
              image: otherImage,
              value: graph.enums.PropertyCondition.Other,
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Condition',
        virtualUrl: 'sell-your-property/condition',
      },
    },
  ],

  [
    StepId.OccupancyStatus,
    {
      id: StepId.OccupancyStatus,
      path: `${sellerFlowPath}/occupancy-status`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.OccupancyStatus,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Who is living in this home?',
      questions: [
        {
          field: 'property.occupancy_status',
          type: QuestionType.GroupRadio,
          validate: requiredFieldValidator('property.occupancy_status'),
          options: [
            { label: 'It is occupied by the owner', value: 'OWNER_OCCUPIED' },
            {
              label: 'It is occupied by a rent-paying tenant',
              value: 'TENANT',
            },
            {
              label: 'It is occupied by a non-paying tenant',
              value: 'NON_PAYING_TENANT',
            },
            {
              label: 'It is vacant / not applicable',
              value: 'VACANT',
            },
          ],
        },
      ],
    },
  ],
  [
    StepId.OccupancyDescription,
    {
      id: StepId.OccupancyDescription,
      path: `${sellerFlowPath}/occupancy-description`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.OccupancyDescription,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Who is living in this home?',
      questions: [
        {
          field: 'property.occupancy_description',
          type: QuestionType.GroupRadio,
          validate: requiredFieldValidator('property.occupancy_description'),
          options: [
            {
              label: 'I live in the home',
              value: graph.enums.OccupancyDescriptionType.OwnerOccupied,
            },
            {
              label: 'A tenant',
              value: graph.enums.OccupancyDescriptionType.TenantOccupied,
            },
            {
              label: `It's vacant`,
              value: graph.enums.OccupancyDescriptionType.Vacant,
            },
            // TBD For Future option: This will be used once we know the flow
            // {
            //   label: 'adversely occupied',
            //   value: 'graph.enums.OccupancyDescriptionType.AdverselyOccupied',
            // },
          ],
        },
      ],
    },
  ],
  [
    StepId.Mortgage,
    {
      id: StepId.Mortgage,
      path: `${sellerFlowPath}/active-mortgage`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.Mortgage, answer, featureFlags, sellerSteps),
      title: 'Is there an active mortgage on the property?',
      questions: [
        {
          // TODO mortgage field in graph
          field: 'property.mortgage',
          type: QuestionType.Radio,
          validate: requiredFieldValidator('property.mortgage'),
          options: [
            {
              label: 'Yes, and the mortgage is current',
              value: 'ACTIVE_MORTGAGE',
            },
            {
              label: 'Yes, but the mortgage is past due',
              value: 'EXPIRED_MORTGAGE',
            },
            {
              label: 'No',
              value: 'NONE',
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Active Mortgage',
        virtualUrl: 'sell-your-property/active-mortgage',
      },
    },
  ],
  [
    StepId.PriceRange,
    {
      id: StepId.PriceRange,
      path: `${sellerFlowPath}/property-valuation`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.PriceRange, answer, featureFlags, sellerSteps),
      title: 'What is your estimated market value for the property?',
      questions: [
        {
          label: 'Low End',
          validate: requiredFieldValidator(
            'property.desired_amount.lower_bound'
          ),
          field: 'property.desired_amount.lower_bound',
          valueParser: answer => createBound(Number(answer), true, true),
          type: QuestionType.Currency,
        },
        {
          label: 'High End',
          validate: requiredFieldValidator(
            'property.desired_amount.upper_bound'
          ),
          field: 'property.desired_amount.upper_bound',
          valueParser: answer => createBound(Number(answer), true, true),
          type: QuestionType.Currency,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Property Valuation',
        virtualUrl: 'sell-your-property/property-valuation',
      },
    },
  ],
  [
    StepId.RelationToProperty,
    {
      id: StepId.RelationToProperty,
      path: `${sellerFlowPath}/relationship-to-property`,
      next: ({ answer, featureFlags }: NextStepData) => {
        return determineNextStep(
          StepId.RelationToProperty,
          answer,
          featureFlags,
          sellerSteps
        )
      },
      title: 'What is your relation to this property?',
      questions: [
        {
          field: 'property.relationship_to_property',
          type: QuestionType.Radio,
          validate: requiredFieldValidator('property.relationship_to_property'),
          options: [
            {
              label: 'I am the owner',
              value: 'OWNER',
            },
            {
              label: 'I am a joint owner',
              value: 'PART_OWNER',
            },
            {
              label: 'I am the agent',
              value: 'REPRESENTATIVE_OF_OWNER',
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Relationship to Property',
        virtualUrl: 'sell-your-property/relationship-to-property',
      },
    },
  ],
  [
    StepId.Urgency,
    {
      id: StepId.Urgency,
      path: `${sellerFlowPath}/sell-timeline`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.Urgency, answer, featureFlags, sellerSteps),
      title: 'When are you looking to sell the property?',
      questions: [
        {
          field: 'property.sale_timeline',
          type: QuestionType.GroupRadio,
          validate: requiredFieldValidator('property.sale_timeline'),
          defaultValue: answers => {
            const sellerIntent = answers?.['property.seller_intent']
            if (sellerIntent === graph.enums.SellerIntentType.Speed) {
              return SaleTimeline.Immediately
            } else if (sellerIntent === graph.enums.SellerIntentType.Value) {
              return SaleTimeline.ThreeMonths
            }
            return ''
          },
          options: [
            {
              label: 'Immediately',
              value: SaleTimeline.Immediately,
            },
            {
              label: 'Within 3 months',
              value: SaleTimeline.ThreeMonths,
            },
            {
              label: 'Between 3 to 6 months',
              value: SaleTimeline.ThreeToSixMonths,
            },
            {
              label: "More than 6 months or I'm just looking",
              value: SaleTimeline.SixPlusMonths,
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Sell Timeline',
        virtualUrl: 'sell-your-property/sell-timeline',
      },
    },
  ],
  [
    StepId.ContactInformation,
    {
      id: StepId.ContactInformation,
      path: `${sellerFlowPath}/contact-information`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.ContactInformation,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Please provide your contact information',
      subtitle:
        'We will reach out to discuss details of the program and provide you instructions on how to move forward.',
      isSubmitStep: () => true,
      questions: [
        {
          label: 'First Name',
          field: 'first_name',
          validate: requiredFieldValidator('first_name'),
          type: QuestionType.Input,
          required: true,
        },
        {
          label: 'Last Name',
          field: 'last_name',
          validate: requiredFieldValidator('last_name'),
          type: QuestionType.Input,
          required: true,
        },
        {
          label: 'Email',
          field: 'email',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.email
            )

            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : emailValidator(answers.email)
          },
          type: QuestionType.Input,
          required: true,
        },
        {
          label: 'Phone',
          field: 'phone',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.phone
            )
            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : phoneValidator(answers.phone)
          },
          type: QuestionType.PhoneNumber,
          required: true,
        },
        {
          label: 'Preferred Contact Method',
          field: 'preferred_contact_method',
          type: QuestionType.Select,
          options: contactMethodOptions,
        },
      ],
      stepStyles: ['contact-information-scroll'],
      text: () => <ContactInformationFooter />,
      ga: {
        virtualTitle: 'Sell Your Property – Contact Information',
        virtualUrl: 'sell-your-property/contact-information',
      },
    },
  ],
  [
    StepId.NewContactInformationDesign,
    {
      id: StepId.NewContactInformationDesign,
      path: `${sellerFlowPath}/add-contact-information`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.NewContactInformationDesign,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: `You're in the right place.`,
      subtitle: (answers: Record<string, any>) => (
        <ContactInformationSubtitle anwser={answers} />
      ),
      isSubmitStep: () => true,
      questions: [
        {
          label: 'First Name',
          field: 'first_name',
          validate: requiredFieldValidator('first_name'),
          type: QuestionType.Input,
        },
        {
          label: 'Last Name',
          field: 'last_name',
          validate: requiredFieldValidator('last_name'),
          type: QuestionType.Input,
        },
        {
          label: 'Email',
          field: 'email',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.email
            )

            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : emailValidator(answers.email)
          },
          type: QuestionType.Input,
          validateOnlyOnBlur: true,
        },
        {
          label: 'Mobile',
          field: 'phone',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.phone
            )
            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : phoneValidator(answers.phone)
          },
          type: QuestionType.PhoneNumber,
          validateOnlyOnBlur: true,
        },
        {
          field: '__computed__.privacy_statement',
          placeholder: <ContactInformationFooter />,
          type: QuestionType.Checkbox,
          validate: requiredFieldValidator('__computed__.privacy_statement'),
          styles: ['common-input'],
          compact: true,
        },
      ],
      stepStyles: ['contact-information-step'],
      ga: {
        virtualTitle: 'Sell Your Property – Contact Information',
        virtualUrl: 'sell-your-property/contact-information',
      },
    },
  ],
  [
    StepId.PasswordlessSignup,
    {
      id: StepId.PasswordlessSignup,
      path: `${sellerFlowPath}/save-your-progress`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.PasswordlessSignup,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: <PasswordlessSignupHeader />,
      subtitle: <PasswordlessSignupSubHeader />,
      isSubmitStep: () => true,
      questions: [
        {
          placeholder: 'First Name',
          field: 'first_name',
          validate: requiredFieldValidator('first_name'),
          type: QuestionType.Input,
          styles: ['compact-dynamic-field'],
          componentProps: {
            hideLabel: true,
          },
          readOnly: leadIntake => isUserLoggedIn(leadIntake),
        },
        {
          placeholder: 'Last Name',
          field: 'last_name',
          validate: requiredFieldValidator('last_name'),
          type: QuestionType.Input,
          styles: ['compact-dynamic-field'],
          componentProps: {
            hideLabel: true,
          },
          readOnly: leadIntake => isUserLoggedIn(leadIntake),
        },
        {
          placeholder: 'Email',
          field: 'email',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.email
            )

            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : emailValidator(answers.email)
          },
          type: QuestionType.Input,
          validateOnlyOnBlur: true,
          styles: ['compact-dynamic-field'],
          componentProps: {
            hideLabel: true,
          },
          readOnly: leadIntake => isUserLoggedIn(leadIntake),
        },
        {
          placeholder: 'Mobile',
          field: 'phone',
          validate: (answers: Record<string, string>) => {
            const requiredValueValidatorResult = requiredValueValidator(
              answers.phone
            )
            return !requiredValueValidatorResult.valid
              ? requiredValueValidatorResult
              : phoneValidator(answers.phone)
          },
          type: QuestionType.PhoneNumber,
          validateOnlyOnBlur: true,
          styles: ['compact-dynamic-field'],
          componentProps: {
            hideLabel: true,
            inputLabel: '+1',
          },
        },
        {
          field: '__computed__.privacy_statement',
          placeholder: <ContactInformationFooter />,
          type: QuestionType.Checkbox,
          validate: requiredFieldValidator('__computed__.privacy_statement'),
          styles: ['common-input'],
          compact: true,
        },
      ],
      stepStyles: ['contact-information-step'],
      ga: {
        virtualTitle: 'Sell Your Property – Let´s get you started',
        virtualUrl: 'sell-your-property/save-your-progress',
      },
    },
  ],
  [
    StepId.WelcomeBack,
    {
      id: StepId.WelcomeBack,
      path: `${sellerFlowPath}/welcome-back`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.WelcomeBack,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Welcome back!',
      subtitle: () => (
        <Text size='large' className={cx('u-mb-2')} elmId='step-subtitle-text'>
          It looks like you already have an account. Please log in to continue.
        </Text>
      ),
      // isLastStep: () => true,
      questions: [
        {
          field: 'password',
          type: QuestionType.Password,
        },
      ],
      stepStyles: ['contact-information-step'],
      ga: {
        virtualTitle: 'Sell Your Property – Welcome Back',
        virtualUrl: 'sell-your-property/welcome-back',
      },
    },
  ],
  [
    StepId.Authentication,
    {
      id: StepId.Authentication,
      path: `${sellerFlowPath}/account-info`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.Authentication,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Want to make things a bit easier in the future?',
      questions: [
        {
          field: 'authentication',
          type: QuestionType.Authentication,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Account Info',
        virtualUrl: '"sell-your-property/account-info',
      },
      isLastStep: () => true,
    },
  ],
  [
    StepId.ThankYou,
    {
      id: StepId.ThankYou,
      path: `${sellerFlowPath}/thank-you`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.ThankYou, answer, featureFlags, sellerSteps),
      isLastStep: () => true,
      title: 'Selling on your terms starts now.',
      text: () => (
        <section className={cx('section')}>
          <Row className={cx('row')}>
            <Column className={cx('container')}>
              <div className={cx('contact-text')}>
                <Image src={smartsaleLogo} alt='smartsale.com' centered />
                <Text
                  size='large'
                  className={cx('u-mt-4')}
                  elmId='thank-you-text-0'
                >
                  Our SmartSale concierge will be in touch within 1 business
                  day—often sooner.
                </Text>
                <Text size='large' elmId='thank-you-text-1'>
                  Want to speak now? Give us a call at 855-322-3050
                </Text>
                <Text size='large' elmId='thank-you-text-2'>
                  (Mon-Fri, 6am to 5pm Pacific Time).
                </Text>
              </div>
            </Column>
          </Row>
        </section>
      ),
      stepStyles: ['thank-you-step'],
      ga: {
        virtualTitle: 'Sell Your Property – Thank You',
        virtualUrl: 'sell-your-property/thank-you',
      },
    },
  ],
  [
    StepId.LandSale,
    {
      id: StepId.LandSale,
      path: `${sellerFlowPath}/land-sale`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(StepId.LandSale, answer, featureFlags, sellerSteps),
      title: 'Land sale flow is being built...',
      questions: [
        {
          label: "Please enter the address of the land you're selling.",
          field: 'address',
          validate: requiredFieldValidator('address'),
          type: QuestionType.Input,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property - Land Sale',
        virtualUrl: 'sell-your-property/land-sale',
      },
    },
  ],
  [
    StepId.AgentOrHomeowner,
    {
      id: StepId.AgentOrHomeowner,
      path: `${sellerFlowPath}/have-agent`,
      next: ({ answer, featureFlags }: NextStepData) => {
        return determineNextStep(
          StepId.AgentOrHomeowner,
          answer,
          featureFlags,
          sellerSteps
        )
      },
      title: 'Do you have an agent?',
      questions: [
        {
          field: 'property.relationship_to_property',
          validate: requiredFieldValidator('property.relationship_to_property'),
          type: QuestionType.GroupRadio,
          options: [
            {
              label: 'No, I do not have an agent',
              value: graph.enums.RelationshipToProperty.Owner,
            },
            {
              label: 'Yes, I have an agent',
              value: graph.enums.RelationshipToProperty.OwnerWithAgent,
            },
            {
              label: 'I am an agent on behalf of the homeowner',
              value: graph.enums.RelationshipToProperty.ListingAgent,
            },
          ],
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Have Agent',
        virtualUrl: 'sell-your-property/have-agent',
      },
    },
  ],
  [
    StepId.AgentInformation,
    {
      id: StepId.AgentInformation,
      path: `${sellerFlowPath}/agent-info`,
      next: ({ answer, featureFlags }: NextStepData) =>
        determineNextStep(
          StepId.AgentInformation,
          answer,
          featureFlags,
          sellerSteps
        ),
      title: 'Who is the agent helping you to sell your home?',
      questions: [
        {
          label: `Agent's Name`,
          placeholder: 'Name (optional)',
          field: 'property.agent_first_name',
          type: QuestionType.Input,
        },
        {
          label: `Agent's Email Address`,
          placeholder: 'Email (optional)',
          field: 'property.agent_email',
          validate: (answers: Record<string, string>) => {
            return _.isEmpty(answers['property.agent_email'])
              ? { valid: true }
              : emailValidator(answers['property.agent_email'])
          },
          type: QuestionType.Input,
          validateOnlyOnBlur: true,
        },
      ],
      ga: {
        virtualTitle: 'Sell Your Property – Agent Info',
        virtualUrl: 'sell-your-property/agent-info',
      },
    },
  ],
])
